"use client";

import { useSession } from "@saas/auth/hooks/use-session";
import { creditsQueryKey, useCredits } from "@saas/credits/hooks/use-credits";
import { CreditsPaywallModal } from "@saas/payments/components/CreditsPaywallModal";
import { PromptToolWorkbench } from "@saas/shared/components/PromptToolWorkbench";
import { apiClient } from "@shared/lib/api-client";
import { reportGenerationFailure } from "@shared/lib/report-generation-failure";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Input } from "@ui/components/input";
import { Label } from "@ui/components/label";
import { Textarea } from "@ui/components/textarea";
import { cn } from "@ui/lib";
import { Loader2Icon, SparklesIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
	AI_MUSIC_PAGE_PATH,
	MUSIC_GENERATION_COST,
	MUSIC_LIMITS,
	MUSIC_MODEL_OPTIONS,
	MUSIC_POLL_INTERVAL_MS,
	type MusicModelId,
	STYLE_TAG_GROUPS,
} from "../config";
import type { MusicFormValues } from "../types";
import { MUSIC_REUSE_STORAGE_KEY } from "./MusicAssets";
import { MUSIC_EXAMPLE_EVENT, MusicExamplesPanel } from "./MusicExamples";
import { MUSIC_HISTORY_QUERY_KEY, MusicFeed } from "./MusicFeed";

type Mode = "simple" | "custom";
type Vocal = "auto" | "m" | "f";

const SEGMENT =
	"flex-1 rounded-full px-3 py-1.5 text-sm font-medium transition-colors";
const SEGMENT_ON = "bg-zinc-950 text-white shadow-sm";
const SEGMENT_OFF = "text-zinc-600 hover:text-zinc-950";

function Segmented<T extends string>({
	value,
	options,
	onChange,
	label,
}: {
	value: T;
	options: { id: T; label: string }[];
	onChange: (value: T) => void;
	label: string;
}) {
	return (
		<div
			aria-label={label}
			className="flex gap-1 rounded-full bg-zinc-100 p-1 dark:bg-muted"
		>
			{options.map((option) => (
				<button
					key={option.id}
					type="button"
					aria-pressed={value === option.id}
					onClick={() => onChange(option.id)}
					className={cn(
						SEGMENT,
						value === option.id ? SEGMENT_ON : SEGMENT_OFF,
					)}
				>
					{option.label}
				</button>
			))}
		</div>
	);
}

function CharCount({ value, max }: { value: string; max: number }) {
	return (
		<span className="text-muted-foreground text-xs tabular-nums">
			{value.length}/{max}
		</span>
	);
}

/** 点标签把它追加进 Styles；已经有了就去掉 */
function toggleTag(style: string, tag: string) {
	const parts = style
		.split(",")
		.map((part) => part.trim())
		.filter(Boolean);
	const next = parts.some((part) => part.toLowerCase() === tag)
		? parts.filter((part) => part.toLowerCase() !== tag)
		: [...parts, tag];
	return next.join(", ").slice(0, MUSIC_LIMITS.style);
}

export function MusicGeneratorClient({
	title,
	description,
}: {
	title?: string;
	description?: string;
}) {
	const t = useTranslations("aiMusic.workbench");
	const tRoot = useTranslations("aiMusic");
	const { user } = useSession();
	const { getRemainingCreditsWhenLoaded } = useCredits();
	const queryClient = useQueryClient();

	const [mode, setMode] = useState<Mode>("custom");
	const [model, setModel] = useState<MusicModelId>("V6");
	const [instrumental, setInstrumental] = useState(false);
	const [vocal, setVocal] = useState<Vocal>("auto");
	const [prompt, setPrompt] = useState("");
	const [songTitle, setSongTitle] = useState("");
	const [style, setStyle] = useState("");
	const [lyrics, setLyrics] = useState("");
	const [submitting, setSubmitting] = useState(false);
	const [writingLyrics, setWritingLyrics] = useState(false);
	const [paywallOpen, setPaywallOpen] = useState(false);

	const requireLogin = () => {
		if (user) {
			return false;
		}
		window.dispatchEvent(
			new CustomEvent("show-login", {
				detail: {
					redirectTo:
						window.location.pathname + window.location.search,
				},
			}),
		);
		return true;
	};

	const reuse = (values: MusicFormValues) => {
		setMode(values.mode);
		setModel(values.model);
		setInstrumental(values.instrumental);
		setVocal(values.vocalGender ?? "auto");
		setPrompt(values.prompt ?? "");
		setSongTitle(values.title ?? "");
		setStyle(values.style ?? "");
		setLyrics(values.lyrics ?? "");
		document
			.getElementById("prompt-tool-workbench")
			?.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	// 样片的「Use these settings」（营销区和右栏都在发）
	const reuseRef = useRef(reuse);
	reuseRef.current = reuse;
	useEffect(() => {
		const onExample = (event: Event) =>
			reuseRef.current((event as CustomEvent<MusicFormValues>).detail);
		window.addEventListener(MUSIC_EXAMPLE_EVENT, onExample);
		return () => window.removeEventListener(MUSIC_EXAMPLE_EVENT, onExample);
	}, []);

	// My Assets 点 Reuse 跳过来：读一次就删
	useEffect(() => {
		try {
			const saved = sessionStorage.getItem(MUSIC_REUSE_STORAGE_KEY);
			if (saved) {
				sessionStorage.removeItem(MUSIC_REUSE_STORAGE_KEY);
				reuseRef.current(JSON.parse(saved) as MusicFormValues);
			}
		} catch {
			// 存储被禁或内容坏了，就当没有
		}
	}, []);

	const writeLyricsWithAi = async () => {
		if (requireLogin()) {
			return;
		}
		const idea = [
			songTitle && `A song titled "${songTitle}"`,
			style && `in the style of ${style}`,
		]
			.filter(Boolean)
			.join(" ")
			.slice(0, MUSIC_LIMITS.lyricsPrompt);
		if (!idea) {
			toast.info(t("lyricsNeedIdea"));
			return;
		}
		setWritingLyrics(true);
		try {
			const response = await apiClient.music.lyrics.$post({
				json: { prompt: idea },
			});
			if (!response.ok) {
				throw new Error("Failed to write lyrics");
			}
			const { id } = await response.json();
			for (let attempt = 0; attempt < 40; attempt++) {
				await new Promise((resolve) =>
					setTimeout(resolve, MUSIC_POLL_INTERVAL_MS),
				);
				const poll = await apiClient.music.lyrics[":id"].$get({
					param: { id },
				});
				if (!poll.ok) {
					continue;
				}
				const result = await poll.json();
				if (result.status === "SUCCESS" && result.text) {
					setLyrics(result.text.slice(0, MUSIC_LIMITS.lyrics));
					return;
				}
				if (result.status === "FAILED") {
					break;
				}
			}
			throw new Error("Lyrics generation failed");
		} catch (error) {
			console.error(error);
			toast.error(t("lyricsFailed"));
		} finally {
			setWritingLyrics(false);
		}
	};

	const handleCreate = async () => {
		if (requireLogin() || submitting) {
			return;
		}
		if (mode === "simple" && !prompt.trim()) {
			toast.info(t("needPrompt"));
			return;
		}
		if (
			mode === "custom" &&
			!style.trim() &&
			(instrumental || !lyrics.trim())
		) {
			toast.info(t("needStyleOrLyrics"));
			return;
		}
		const remaining =
			await getRemainingCreditsWhenLoaded("IMAGE_GENERATION");
		// null：积分还在加载、又点了一次，忽略这次
		if (remaining === null) {
			return;
		}
		if (remaining < MUSIC_GENERATION_COST) {
			setPaywallOpen(true);
			return;
		}

		setSubmitting(true);
		try {
			const response = await apiClient.music.generate.$post({
				json: {
					mode,
					model,
					instrumental,
					page: AI_MUSIC_PAGE_PATH,
					...(mode === "simple"
						? { prompt: prompt.trim() }
						: {
								title: songTitle.trim() || undefined,
								style: style.trim() || undefined,
								lyrics: instrumental
									? undefined
									: lyrics.trim() || undefined,
								vocalGender:
									!instrumental && vocal !== "auto"
										? vocal
										: undefined,
							}),
				},
			});
			if (response.status === 402) {
				setPaywallOpen(true);
				return;
			}
			if (response.status === 400) {
				const body = (await response.json().catch(() => ({}))) as {
					error?: string;
				};
				if (body.error === "CONTENT_MODERATION_BLOCKED") {
					reportGenerationFailure(
						"music",
						model,
						"moderation blocked",
					);
					toast.error(t("blocked"));
					return;
				}
			}
			if (!response.ok) {
				throw new Error(`Failed to start: ${response.status}`);
			}
			// 新行以 PENDING 进列表，卡片自己轮询
			await queryClient.invalidateQueries({
				queryKey: MUSIC_HISTORY_QUERY_KEY,
			});
			queryClient.invalidateQueries({ queryKey: creditsQueryKey });
		} catch (error) {
			console.error(error);
			reportGenerationFailure("music", model, error);
			toast.error(t("startFailed"));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<PromptToolWorkbench
				title={title ?? t("title")}
				description={description ?? t("description")}
				action={
					<Button
						onClick={handleCreate}
						disabled={submitting}
						className="bg-zinc-950 text-white hover:bg-zinc-800"
					>
						{submitting ? (
							<>
								<Loader2Icon className="mr-2 size-4 animate-spin" />
								{t("creating")}
							</>
						) : (
							t("create", { credits: MUSIC_GENERATION_COST })
						)}
					</Button>
				}
				result={
					<MusicFeed
						onReuse={reuse}
						emptyState={<MusicExamplesPanel />}
					/>
				}
				resultTitle={tRoot("feed.title")}
				emptyLabel={tRoot("feed.empty")}
			>
				<div className="space-y-5">
					<Segmented
						label={t("modes.custom")}
						value={mode}
						onChange={setMode}
						options={[
							{ id: "simple", label: t("modes.simple") },
							{ id: "custom", label: t("modes.custom") },
						]}
					/>

					{mode === "simple" ? (
						<div className="space-y-2">
							<div className="flex items-center justify-between">
								<Label htmlFor="music-prompt">
									{t("prompt.label")}
								</Label>
								<CharCount
									value={prompt}
									max={MUSIC_LIMITS.prompt}
								/>
							</div>
							<Textarea
								id="music-prompt"
								value={prompt}
								maxLength={MUSIC_LIMITS.prompt}
								onChange={(event) =>
									setPrompt(event.target.value)
								}
								placeholder={t("prompt.placeholder")}
								className="min-h-32 resize-y"
							/>
						</div>
					) : (
						<>
							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label htmlFor="music-title">
										{t("songTitle.label")}
									</Label>
									<CharCount
										value={songTitle}
										max={MUSIC_LIMITS.title}
									/>
								</div>
								<Input
									id="music-title"
									value={songTitle}
									maxLength={MUSIC_LIMITS.title}
									onChange={(event) =>
										setSongTitle(event.target.value)
									}
									placeholder={t("songTitle.placeholder")}
								/>
							</div>

							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label htmlFor="music-style">
										{t("style.label")}
									</Label>
									<CharCount
										value={style}
										max={MUSIC_LIMITS.style}
									/>
								</div>
								<Textarea
									id="music-style"
									value={style}
									maxLength={MUSIC_LIMITS.style}
									onChange={(event) =>
										setStyle(event.target.value)
									}
									placeholder={t("style.placeholder")}
									className="min-h-20 resize-y"
								/>
								<div className="space-y-2 pt-1">
									{STYLE_TAG_GROUPS.map((group) => (
										<div
											key={group.key}
											className="flex gap-2"
										>
											<span className="w-12 shrink-0 pt-1 text-muted-foreground text-xs">
												{t(`tagGroups.${group.key}`)}
											</span>
											<div className="flex flex-wrap gap-1.5">
												{group.tags.map((tag) => {
													const active = style
														.toLowerCase()
														.split(",")
														.map((part) =>
															part.trim(),
														)
														.includes(tag);
													return (
														<button
															key={tag}
															type="button"
															aria-pressed={
																active
															}
															onClick={() =>
																setStyle(
																	(current) =>
																		toggleTag(
																			current,
																			tag,
																		),
																)
															}
															className={cn(
																"rounded-full border px-2.5 py-0.5 text-xs transition-colors",
																active
																	? "border-zinc-950 bg-zinc-950 text-white"
																	: "border-zinc-200 text-zinc-700 hover:border-zinc-400 dark:border-border dark:text-foreground",
															)}
														>
															{tag}
														</button>
													);
												})}
											</div>
										</div>
									))}
								</div>
							</div>

							{!instrumental && (
								<div className="space-y-2">
									<div className="flex items-center justify-between">
										<Label htmlFor="music-lyrics">
											{t("lyrics.label")}
										</Label>
										<CharCount
											value={lyrics}
											max={MUSIC_LIMITS.lyrics}
										/>
									</div>
									<Textarea
										id="music-lyrics"
										value={lyrics}
										maxLength={MUSIC_LIMITS.lyrics}
										onChange={(event) =>
											setLyrics(event.target.value)
										}
										placeholder={t("lyrics.placeholder")}
										className="min-h-40 resize-y"
									/>
									<Button
										type="button"
										variant="outline"
										size="sm"
										disabled={writingLyrics}
										onClick={writeLyricsWithAi}
									>
										{writingLyrics ? (
											<Loader2Icon className="mr-1.5 size-3.5 animate-spin" />
										) : (
											<SparklesIcon className="mr-1.5 size-3.5" />
										)}
										{writingLyrics
											? t("writingLyrics")
											: t("writeLyrics")}
									</Button>
								</div>
							)}
						</>
					)}

					<div className="flex items-center justify-between gap-3 rounded-xl bg-zinc-50 px-3 py-2.5 dark:bg-muted/40">
						<div>
							<p className="font-medium text-sm">
								{t("instrumental")}
							</p>
							<p className="text-muted-foreground text-xs">
								{t("instrumentalHint")}
							</p>
						</div>
						<button
							type="button"
							role="switch"
							aria-checked={instrumental}
							aria-label={t("instrumental")}
							onClick={() => setInstrumental((value) => !value)}
							className={cn(
								"relative h-6 w-11 shrink-0 rounded-full transition-colors",
								instrumental ? "bg-zinc-950" : "bg-zinc-300",
							)}
						>
							<span
								className={cn(
									"absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform",
									instrumental && "translate-x-5",
								)}
							/>
						</button>
					</div>

					{mode === "custom" && !instrumental && (
						<div className="space-y-2">
							<Label>{t("vocal.label")}</Label>
							<Segmented
								label={t("vocal.label")}
								value={vocal}
								onChange={setVocal}
								options={[
									{ id: "auto", label: t("vocal.auto") },
									{ id: "f", label: t("vocal.f") },
									{ id: "m", label: t("vocal.m") },
								]}
							/>
						</div>
					)}

					<div className="space-y-2">
						<Label>{t("model.label")}</Label>
						<Segmented
							label={t("model.label")}
							value={model}
							onChange={setModel}
							options={MUSIC_MODEL_OPTIONS.map((option) => ({
								id: option.id,
								label: t(`model.${option.labelKey}`),
							}))}
						/>
						<p className="text-muted-foreground text-xs">
							{t(
								model === "V6"
									? "model.standardHint"
									: "model.creativeHint",
							)}
						</p>
					</div>
				</div>
			</PromptToolWorkbench>
			<CreditsPaywallModal
				open={paywallOpen}
				onOpenChange={setPaywallOpen}
				reason="insufficient-credits"
			/>
		</>
	);
}
